import { useState, useRef } from 'react';
import React from 'react';
import type { PipelineStep, MultiPageProgress } from '@/components/ai/StreamingProgressIndicator';
import type { AgentVisualResponse, CanvasAction, Skill, ProactiveSuggestion } from '@/services/api/agentApi';
import type { SnapshotDiff } from '@/services/editor/CanvasActionApplicator';

// ── Deps (external values the hook needs from the component) ──────────────────
export interface UseAIChatStateDeps {
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  handleSendMessageRef: React.MutableRefObject<(() => Promise<void> | void) | undefined>;
}

// ── Return shape: union of all 5 consolidated hooks ───────────────────────────
export interface UseAIChatStateReturn {
  // --- from useStreamingState ---
  streamingSteps: PipelineStep[];
  setStreamingSteps: React.Dispatch<React.SetStateAction<PipelineStep[]>>;
  streamingMessage: string | null;
  setStreamingMessage: React.Dispatch<React.SetStateAction<string | null>>;
  isStreamingActive: boolean;
  setIsStreamingActive: React.Dispatch<React.SetStateAction<boolean>>;
  multiPageProgress: MultiPageProgress | null;
  setMultiPageProgress: React.Dispatch<React.SetStateAction<MultiPageProgress | null>>;
  pendingConfirmation: AgentVisualResponse | null;
  setPendingConfirmation: React.Dispatch<React.SetStateAction<AgentVisualResponse | null>>;
  pendingChangeSummary: SnapshotDiff | null;
  setPendingChangeSummary: React.Dispatch<React.SetStateAction<SnapshotDiff | null>>;
  streamingActionCountRef: React.MutableRefObject<number>;
  pendingConfirmationActionsRef: React.MutableRefObject<CanvasAction[]>;

  // --- from useModalManager ---
  conversationPopoverOpen: boolean;
  setConversationPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showSkillsModal: boolean;
  setShowSkillsModal: React.Dispatch<React.SetStateAction<boolean>>;
  showAgentsModal: boolean;
  setShowAgentsModal: React.Dispatch<React.SetStateAction<boolean>>;
  showDebugSuite: boolean;
  setShowDebugSuite: React.Dispatch<React.SetStateAction<boolean>>;
  roadmapModalOpen: boolean;
  setRoadmapModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  detailsModalOpen: boolean;
  setDetailsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  iconPickerOpen: boolean;
  setIconPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  colorPickerOpen: boolean;
  setColorPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  colorCommandPrefix: string;
  setColorCommandPrefix: React.Dispatch<React.SetStateAction<string>>;
  colorPropertyLabel: string;
  setColorPropertyLabel: React.Dispatch<React.SetStateAction<string>>;
  deviceBindingOpen: boolean;
  setDeviceBindingOpen: React.Dispatch<React.SetStateAction<boolean>>;
  homeAssistantBindingOpen: boolean;
  setHomeAssistantBindingOpen: React.Dispatch<React.SetStateAction<boolean>>;
  removeDataModalOpen: boolean;
  setRemoveDataModalOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // --- from useMessageFeedback ---
  messageFeedback: Record<string, 'positive' | 'negative'>;
  setMessageFeedback: React.Dispatch<React.SetStateAction<Record<string, 'positive' | 'negative'>>>;

  // --- from useProactiveSuggestions ---
  proactiveSuggestions: ProactiveSuggestion[];
  setProactiveSuggestions: React.Dispatch<React.SetStateAction<ProactiveSuggestion[]>>;

  // --- from useComponentSkills ---
  handleSkillInsert: (skill: Skill) => void;
}

export function useAIChatState(deps: UseAIChatStateDeps): UseAIChatStateReturn {
  const { setInputValue, handleSendMessageRef } = deps;

  // ── Streaming state (from useStreamingState) ──────────────────────────────
  const [streamingSteps, setStreamingSteps] = useState<PipelineStep[]>([]);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const [isStreamingActive, setIsStreamingActive] = useState(false);
  const [multiPageProgress, setMultiPageProgress] = useState<MultiPageProgress | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<AgentVisualResponse | null>(null);
  const [pendingChangeSummary, setPendingChangeSummary] = useState<SnapshotDiff | null>(null);
  const streamingActionCountRef = useRef<number>(0);
  const pendingConfirmationActionsRef = useRef<CanvasAction[]>([]);

  // ── Modal state (from useModalManager) ───────────────────────────────────
  const [conversationPopoverOpen, setConversationPopoverOpen] = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [showDebugSuite, setShowDebugSuite] = useState(false);
  const [roadmapModalOpen, setRoadmapModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorCommandPrefix, setColorCommandPrefix] = useState('set color to ');
  const [colorPropertyLabel, setColorPropertyLabel] = useState('Color');
  const [deviceBindingOpen, setDeviceBindingOpen] = useState(false);
  const [homeAssistantBindingOpen, setHomeAssistantBindingOpen] = useState(false);
  const [removeDataModalOpen, setRemoveDataModalOpen] = useState(false);

  // ── Feedback state (from useMessageFeedback) ──────────────────────────────
  const [messageFeedback, setMessageFeedback] = useState<Record<string, 'positive' | 'negative'>>({});

  // ── Proactive suggestions (from useProactiveSuggestions) ──────────────────
  const [proactiveSuggestions, setProactiveSuggestions] = useState<ProactiveSuggestion[]>([]);

  // ── Skill insert handler (from useComponentSkills) ────────────────────────
  function handleSkillInsert(skill: Skill) {
    setShowSkillsModal(false);
    setInputValue(`/skill ${skill.name}`);
    // Trigger send after state update
    setTimeout(() => handleSendMessageRef.current?.(), 50);
  }

  return {
    // streaming
    streamingSteps, setStreamingSteps,
    streamingMessage, setStreamingMessage,
    isStreamingActive, setIsStreamingActive,
    multiPageProgress, setMultiPageProgress,
    pendingConfirmation, setPendingConfirmation,
    pendingChangeSummary, setPendingChangeSummary,
    streamingActionCountRef,
    pendingConfirmationActionsRef,
    // modals
    conversationPopoverOpen, setConversationPopoverOpen,
    showSkillsModal, setShowSkillsModal,
    showAgentsModal, setShowAgentsModal,
    showDebugSuite, setShowDebugSuite,
    roadmapModalOpen, setRoadmapModalOpen,
    detailsModalOpen, setDetailsModalOpen,
    iconPickerOpen, setIconPickerOpen,
    colorPickerOpen, setColorPickerOpen,
    colorCommandPrefix, setColorCommandPrefix,
    colorPropertyLabel, setColorPropertyLabel,
    deviceBindingOpen, setDeviceBindingOpen,
    homeAssistantBindingOpen, setHomeAssistantBindingOpen,
    removeDataModalOpen, setRemoveDataModalOpen,
    // feedback
    messageFeedback, setMessageFeedback,
    // proactive suggestions
    proactiveSuggestions, setProactiveSuggestions,
    // skills
    handleSkillInsert,
  };
}
